/**
 * Web Linking Implementation
 */

import type { OpenLinkResult, ShareOptions, ShareResult } from "./types";

/**
 * Opens a URL in a new browser tab/window
 */
export const openBrowser = (url: string): void => {
  window.open(url, "_blank");
};

/**
 * Opens a URL in the same window
 */
export const openLink = async (url: string): Promise<OpenLinkResult> => {
  try {
    window.open(url, "_blank");
    return { success: true };
  } catch (error) {
    return { success: false, error: error as Error };
  }
};

/**
 * Checks if the browser supports the Web Share API
 */
export const canShare = (): boolean => {
  return typeof navigator !== "undefined" && !!navigator.share;
};

/**
 * Shares content using the Web Share API
 */
export const share = async (options: ShareOptions): Promise<ShareResult> => {
  const { url, message } = options;

  if (!canShare()) {
    return {
      success: false,
      error: new Error("Web Share API not supported"),
    };
  }

  try {
    await navigator.share({ url, text: message });
    return { success: true };
  } catch (error) {
    if (error instanceof Error) {
      const errString = error.toString();
      // User cancelled - not an error
      if (errString.includes("AbortError") || errString.includes("InvalidStateError")) {
        return { success: false };
      }
    }
    return { success: false, error: error as Error };
  }
};

/**
 * Downloads a file from a URL
 */
export const downloadFromUrl = async (url: string, fileName: string): Promise<void> => {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error("Network response was not ok");

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const downloadLink = document.createElement("a");
    downloadLink.href = blobUrl;
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    // Clean up the blob URL
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

/**
 * Opens WhatsApp with a pre-filled message
 */
export const openWhatsApp = (phoneNumber: string, message = ""): void => {
  const phone = phoneNumber.replace(/\D/g, "");
  const encodedMessage = encodeURIComponent(message);
  openBrowser(`https://wa.me/${phone}?text=${encodedMessage}`);
};
